import { INTENT_GET_ACTIVE_DISCOUNTS } from "@/constants/global-constants";
import { APP_INDEX_INTENTS } from "@/constants/app-index";
import {
  COLLECT_SESSION_USER_DATA_INTENT,
  RESTORE_SUPPORT_CHAT_IDENTITY_INTENT,
} from "@/constants/app-initialization";
import type { ShouldRevalidateFunction } from "@remix-run/react";

/**
 * Action intents that do not change page data and must not trigger the editor's
 * expensive loader. Initialization updates the client store from its response.
 */
export const NON_MUTATING_DISCOUNT_EDITOR_INTENTS = new Set<string>([
  COLLECT_SESSION_USER_DATA_INTENT,
  RESTORE_SUPPORT_CHAT_IDENTITY_INTENT,
  INTENT_GET_ACTIVE_DISCOUNTS,
  APP_INDEX_INTENTS.FETCH_THEME_EMBED_STATUS,
  APP_INDEX_INTENTS.FETCH_PLAN_UPGRADE_REQUIRED_STATUS,
  APP_INDEX_INTENTS.FETCH_THEME_BLOCK_STATUS_SUMMARY,
  APP_INDEX_INTENTS.FETCH_ALL_DISCOUNTS,
  APP_INDEX_INTENTS.UPDATE_SESSION_TOKEN,
]);

export const shouldRevalidateDiscountEditor: ShouldRevalidateFunction = ({
  actionResult,
  defaultShouldRevalidate,
}) => {
  const intent =
    actionResult && typeof actionResult === "object" && "intent" in actionResult
      ? (actionResult as { intent?: unknown }).intent
      : undefined;

  if (
    typeof intent === "string" &&
    NON_MUTATING_DISCOUNT_EDITOR_INTENTS.has(intent)
  ) {
    return false;
  }

  return defaultShouldRevalidate;
};

/** For leaf editor routes only; parent app/plan loaders must still refresh. */
export const shouldRevalidateDiscountEditorAfterSave: ShouldRevalidateFunction = (args) => {
  const result = args.actionResult;

  // A successful creation is followed by client-side navigation to its saved
  // ID. Loading the old /new (or duplicate) editor in between is unnecessary.
  // Use only on leaf editor routes: parent loaders still refresh app/plan data.
  if (
    args.formMethod?.toUpperCase() === "POST" &&
    result?.success === true &&
    result.isNew === true &&
    typeof result.discountId === "string" &&
    result.discountId.length > 0 &&
    args.currentUrl.href === args.nextUrl.href &&
    args.nextParams.id !== result.discountId
  ) {
    return false;
  }

  return shouldRevalidateDiscountEditor(args);
};
